import { Heading, Icon, Tag, Text } from "@ttc3k/trekker";
import {
  EnumDiscountType,
  EnumDiscountUnit,
  EnumPriceTierPriceType,
  EnumProductType,
  EnumRoomType,
  useDiscountByIdsQuery,
} from "gql/generated";
import { Bed, Lock, ProfileCircle } from "iconoir-react";
import { useTranslation } from "react-i18next";
import { autoGravity } from "@cloudinary/transformation-builder-sdk/qualifiers/gravity";
import { limitFill } from "@cloudinary/url-gen/actions/resize";
import { CloudinaryImage } from "shared/components";
import {
  formatPrice,
  getResolvedProductSellingConfig,
  getLowestNightlyPrice,
  getMultilingualString,
  getDiscountNameString,
  getDiscountUnitInfoString,
} from "shared/utils";
import { css } from "styled-system/css";
import { Center, Flex } from "styled-system/jsx";
import {
  useBookerContext,
  useCartContext,
} from "widgets/booker/contexts";
import {
  getOverriddenDiscountCallout,
  getOverriddenPricingCallout,
  getOverriddenProductName,
} from "widgets/booker/shared/utils";

export const ContentHeader = ({
  imagesExist,
  secureCheckout = false,
}: {
  imagesExist: boolean;
  /** Shows a lock and "Secure checkout" under the title, for the checkout step. */
  secureCheckout?: boolean;
}) => {
  const { product, language, owner, booker, stripeTestMode, isCatalog } =
    useBookerContext();
  const { discounts } = useCartContext();

  const { t } = useTranslation("booker", { keyPrefix: "HEADER" });
  const { t: tCheckout } = useTranslation("booker", { keyPrefix: "CHECKOUT" });
  const sellingConfig = getResolvedProductSellingConfig(
    product as typeof product & { sellingConfig?: any },
  );

  const { data: discountsData } = useDiscountByIdsQuery({
    variables: { ids: discounts.map(d => d.discountId) },
    skip: discounts.length === 0,
  });

  // Only the unconditional "auto-applied" discount type affects this
  // header's price adjustment and discount callout. Early bird,
  // length-of-stay, guest-count, and promo code discounts are all
  // excluded, even though they may apply elsewhere in the cart.
  // Widget/cart discounts still flow into the invoice PricingSummary.
  const activeDiscounts =
    discountsData?.discountByIds
      .filter(discount => discount.type === EnumDiscountType.AutoApplied)
      .map(discount => ({
        id: discount._id,
        unit: discount.unit,
        amount: discount.amount,
        label: `${getDiscountNameString(
          discount.name,
          discount.type,
          discount.promoCode,
          language,
        )} \u2013 ${getDiscountUnitInfoString(
          discount.unit,
          discount.amount,
          tCheckout("OFF"),
        )}`,
      })) ?? [];

  /** Applies a single discount's unit/amount to a price, in cents. */
  const applyDiscountToPrice = (
    price: number,
    discount: { unit: EnumDiscountUnit; amount: number },
  ) =>
    Math.max(
      0,
      discount.unit === EnumDiscountUnit.Percentage
        ? Math.round(price * (1 - discount.amount))
        : price - discount.amount,
    );

  const defaultProductPriceTier = product?.pricing?.tiers?.find(
    v => v?.price && v.price > 0,
  );

  const optionPrices = sellingConfig.options
    .map(option => option.price)
    .filter((p): p is number => typeof p === "number");
  const lowestProductOptionPrice =
    optionPrices.length > 0 ? Math.min(...optionPrices) : null;

  const formatOptionPriceLabel = (price: number) =>
    price === 0
      ? `${t("AS_LOW_AS")} ${t("FREE")}`
      : `${t("STARTING_FROM")} $${formatPrice(price)}`;

  const guestMetadata = product?.accommodationMetadata?.guestMetadata;
  const bedrooms = product?.accommodationMetadata?.rooms?.filter(
    room => room?.type === EnumRoomType.Bed,
  );

  const isPerGuestPricing = product?.pricing?.tiers.some(
    tier => tier?.priceType === EnumPriceTierPriceType.PerGuest,
  );
  const lowestNightlyPrice = getLowestNightlyPrice(product?.pricing);
  const priceText = isPerGuestPricing
    ? `${t("AS_LOW_AS")} $${formatPrice(lowestNightlyPrice ?? 0, lowestNightlyPrice % 100 === 0)}/${t("NIGHT")}`
    : `$${formatPrice(defaultProductPriceTier?.price ?? 0, (defaultProductPriceTier?.price ?? 0) % 100 === 0)}/${t("NIGHT")}`;

  const discountCalloutOverride = getOverriddenDiscountCallout(
    booker,
    language,
  );

  return (
    <Flex
      pb={"600"}
      pt={imagesExist ? "600" : "120px"}
      px={"300"}
      flexDirection={"column"}
      gap={"250"}
      alignItems={"center"}
      w={"full"}
      bg={"white"}
      borderBottomWidth={"1px"}
      borderBottomColor={"border.light"}
      borderBottomStyle={"solid"}
    >
      {stripeTestMode && (
        <Tag visual={"warning"} mode={"dark"}>
          {t("TEST_MODE")}
        </Tag>
      )}
      <Flex flexDirection={"column"} gap={"150"} w={"full"}>
        {booker?.brandingLogo ? (
          <Center>
            <CloudinaryImage
              image={booker.brandingLogo}
              alt={getMultilingualString(owner?.name ?? {}, language)}
              imageStyleProps={{
                className: css({
                  maxW: "130px",
                  aspectRatio: "unset",
                  objectFit: "contain",
                  objectPosition: "center",
                  margin: "0 auto",
                }),
              }}
              resize={limitFill().width(130).gravity(autoGravity())}
            />
          </Center>
        ) : (
          <Text
            color={"text.light"}
            visual={"smallMedium"}
            w={"full"}
            textAlign={"center"}
          >
            {getMultilingualString(owner?.name ?? {}, language)}
          </Text>
        )}
        <Heading size={"h2"} w={"full"} textAlign={"center"}>
          {isCatalog
            ? getOverriddenProductName(
                { name: booker?.name ?? {} } as typeof product,
                booker,
                language,
              )
            : getOverriddenProductName(product, booker, language)}
        </Heading>
        {secureCheckout && (
          <Flex gap={"75"} alignItems={"center"} justifyContent={"center"}>
            <Icon Element={Lock} size={"sm"} color={"icon.mid"} />
            <Text visual={"smallSemiBold"} color={"text.mid"}>
              {t("SECURE_CHECKOUT")}
            </Text>
          </Flex>
        )}
      </Flex>

      {product?.type === EnumProductType.Accommodation &&
        defaultProductPriceTier && (
          <>
            <Tag
              visual={"accent.blue"}
              mode={"light"}
              px={"150!"}
              py={"100"}
              css={{ "&>p": { fontSize: "md", lineHeight: "sm" } }}
            >
              {getOverriddenPricingCallout(booker, priceText, language)}
            </Tag>
            <Flex gap={"150"}>
              {guestMetadata && (
                <Flex gap={"75"} alignItems={"center"}>
                  <Icon
                    Element={ProfileCircle}
                    size={"sm"}
                    color={"icon.light"}
                  />
                  <Text visual={"smallSemiBold"} color={"text.dark"}>
                    {guestMetadata.minimumNumber}
                    {guestMetadata.maximumNumber
                      ? `-${guestMetadata.maximumNumber}`
                      : ""}
                    &nbsp;{t("GUESTS")}
                  </Text>
                </Flex>
              )}
              {product && bedrooms && bedrooms.length > 0 && (
                <Flex gap={"75"} alignItems={"center"}>
                  <Icon Element={Bed} size={"sm"} color={"icon.light"} />
                  <Text visual={"smallSemiBold"} color={"text.dark"}>
                    {bedrooms.length}
                    &nbsp;
                    {t(
                      bedrooms.length === 1 ? "BEDROOMS_ONE" : "BEDROOMS_OTHER",
                    )}
                  </Text>
                </Flex>
              )}
            </Flex>
          </>
        )}
      {!isCatalog &&
        (product?.type === EnumProductType.Fee ||
        product?.type === EnumProductType.RenewableFee ||
        product?.type === EnumProductType.Merch ||
        product?.type === EnumProductType.Event) &&
        (lowestProductOptionPrice != null ||
          sellingConfig.basePrice > 0 ||
          defaultProductPriceTier) && (
          <>
            <Tag
              visual={"accent.blue"}
              mode={"light"}
              px={"150!"}
              py={"100"}
              css={{ "&>p": { fontSize: "md", lineHeight: "sm" } }}
            >
              {getOverriddenPricingCallout(
                booker,
                formatOptionPriceLabel(
                  activeDiscounts.reduce(
                    (price, discount) =>
                      applyDiscountToPrice(price, discount),
                    lowestProductOptionPrice != null
                      ? lowestProductOptionPrice
                      : sellingConfig.basePrice ||
                          defaultProductPriceTier?.price ||
                          0,
                  ),
                ),
                language,
              )}
            </Tag>
            {discountCalloutOverride ? (
              <Flex gap={"100"} flexWrap={"wrap"} justifyContent={"center"}>
                <Tag
                  visual={"success"}
                  mode={"light"}
                  px={"150!"}
                  py={"100"}
                  css={{ "&>p": { fontSize: "md", lineHeight: "sm" } }}
                >
                  {discountCalloutOverride}
                </Tag>
              </Flex>
            ) : (
              activeDiscounts.length > 0 && (
                <Flex gap={"100"} flexWrap={"wrap"} justifyContent={"center"}>
                  {activeDiscounts.map(discount => (
                    <Tag
                      key={discount.id}
                      visual={"success"}
                      mode={"light"}
                      px={"150!"}
                      py={"100"}
                      css={{ "&>p": { fontSize: "md", lineHeight: "sm" } }}
                    >
                      {discount.label}
                    </Tag>
                  ))}
                </Flex>
              )
            )}
          </>
        )}
    </Flex>
  );
};
